import type { DiscoveryApi, DiscoverySource } from '../../data/discovery';
import { laneServedRequests, platformInfoOf } from '../../lib/catalogMap';
import { endpointIdOf, platformIdOf } from '../../lib/catalogPlatform';
import { displayPrice, type DisplayPrice } from '../../lib/pricing';

export interface ProviderEndpoint {
  sku: string;
  name: string;
  platform: string;
  href: string;
  requests: number;
  price: DisplayPrice;
  uptimePct?: number;
  latencyMs?: number;
}

export interface ProviderProfile {
  source: DiscoverySource;
  requests: number;
  endpoints: ProviderEndpoint[];
}

interface MutableProfile {
  source: DiscoverySource;
  endpoints: Map<string, ProviderEndpoint>;
}

const endpointFor = (api: DiscoveryApi, laneIndex: number): ProviderEndpoint => {
  const lane = api.lanes[laneIndex];
  return {
    sku: api.slug,
    name: api.name,
    platform: platformInfoOf(api.slug).name,
    href: `/api/${platformIdOf(api.slug)}/${endpointIdOf(api.slug)}`,
    requests: laneServedRequests(lane),
    price: displayPrice(lane.pricing),
    ...(lane.health === undefined
      ? {}
      : { uptimePct: lane.health.uptimePct, latencyMs: lane.health.latencyP50Ms }),
  };
};

const mergeEndpoint = (current: ProviderEndpoint | undefined, next: ProviderEndpoint) =>
  current === undefined
    ? next
    : {
        ...current,
        requests: current.requests + next.requests,
        price:
          next.price.perRequestUsd < current.price.perRequestUsd ? next.price : current.price,
      };

/** Aggregates the catalog's lane identities into public provider profiles. */
export const providerProfiles = (entries: DiscoveryApi[]): ProviderProfile[] => {
  const profiles = new Map<string, MutableProfile>();
  for (const api of entries) {
    api.lanes.forEach(({ source }, laneIndex) => {
      const profile = profiles.get(source.id) ?? { source, endpoints: new Map() };
      const endpoint = endpointFor(api, laneIndex);
      profile.endpoints.set(api.slug, mergeEndpoint(profile.endpoints.get(api.slug), endpoint));
      profiles.set(source.id, profile);
    });
  }
  return [...profiles.values()]
    .map(({ source, endpoints }) => {
      const ranked = [...endpoints.values()].sort(
        (a, b) => b.requests - a.requests || a.name.localeCompare(b.name),
      );
      return {
        source,
        requests: ranked.reduce((total, endpoint) => total + endpoint.requests, 0),
        endpoints: ranked,
      };
    })
    .sort((a, b) => b.requests - a.requests || a.source.name.localeCompare(b.source.name));
};

export const findProviderProfile = (
  entries: DiscoveryApi[],
  providerId: string,
): ProviderProfile | null =>
  providerProfiles(entries).find((profile) => profile.source.id === providerId) ?? null;
